<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: StructuredLanguageEditor
  Reason: Mapping-table picker for LogicLanguage.mapping plus the read-only parameter contract of the language.
-->

@if (initialized) {

    <div class="flex flex-column gap-4">

        <!-- Picker -->
        <div class="flex flex-column gap-2">
            <div class="flex align-items-center gap-2">
                <span class="text-lg font-semibold">Mapping-Tabelle</span>
                <i class="fa-regular fa-circle-info text-gray-600"
                   pTooltip="Eine Mapping-Logik hat kein Skript. Ihre einzige Konfiguration ist die hier gewählte Wertetabelle."
                   tooltipPosition="top"></i>
            </div>

            @if (busy) {
                <p-skeleton height="2.5rem" width="24rem"></p-skeleton>
            } @else if (loadErrorMessage) {
                <p-message severity="error" [text]="loadErrorMessage"></p-message>
                <div>
                    <p-button label="Erneut versuchen"
                              icon="fa-regular fa-arrows-rotate"
                              [outlined]="true"
                              severity="secondary"
                              (onClick)="loadMappingTables()"></p-button>
                </div>
            } @else if (!mappingTableOptions.length) {
                <p-message severity="warn"
                           text="Es ist keine Mapping-Tabelle angelegt. Legen Sie zuerst unter „Konfiguration → Logikbausteine → Wertetabellen“ eine Tabelle an."></p-message>
            } @else {
                <div class="flex align-items-center gap-2 flex-wrap">
                    <p-select
                            [options]="mappingTableOptions"
                            [(ngModel)]="selectedMappingTableId"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Mapping-Tabelle wählen"
                            [filter]="true"
                            filterBy="label"
                            [showClear]="true"
                            [disabled]="saving"
                            (onChange)="onMappingTableSelected()"
                            styleClass="w-24rem"></p-select>

                    @if (saving) {
                        <i class="fa-regular fa-spinner fa-spin text-gray-600"></i>
                        <span class="text-sm text-gray-600">Wird gespeichert …</span>
                    }
                </div>

                @if (isMappingTableMissing()) {
                    <p-message severity="error"
                               text="Pflichtangabe: Ohne Mapping-Tabelle kann diese Logik nicht ausgeführt werden."></p-message>
                }

                @if (isSelectedTableInactive()) {
                    <p-message severity="warn"
                               text="Diese Mapping-Tabelle ist deaktiviert. Die Logik löst weiterhin unverändert gegen sie auf — für neue Zuordnungen steht sie jedoch nicht mehr zur Verfügung. Wenn Sie hier auf eine andere Tabelle wechseln, lässt sich die deaktivierte nicht mehr zurückwählen."></p-message>
                }
            }
        </div>

        <!-- Short info about the picked table -->
        @if (selectedMappingTable) {
            <div class="surface-ground border-round-lg p-3">
                <div class="flex align-items-center gap-2 mb-3">
                    <span class="text-base font-semibold">Kurzinfo zur Tabelle</span>
                    @if (isSelectedTableInactive()) {
                        <p-tag value="deaktiviert" severity="warn" [rounded]="true"></p-tag>
                    }
                </div>

                <div class="grid">
                    <div class="col-12 md:col-6 mb-2">
                        <div class="text-sm text-gray-600">Alias</div>
                        <div class="text-base">{{ selectedMappingTable.alias || '—' }}</div>
                    </div>
                    <div class="col-12 md:col-6 mb-2">
                        <div class="text-sm text-gray-600">Logikgruppe</div>
                        <div class="text-base">{{ selectedMappingTable.logicGroupDtoName || '—' }}</div>
                    </div>
                    <div class="col-12 md:col-6 mb-2">
                        <div class="text-sm text-gray-600 flex align-items-center gap-1">
                            Quell-Typ
                            <i class="fa-regular fa-circle-info"
                               pTooltip="Reine Beschriftung der Spalten — beschreibt, was Quell- und Zielwerte bedeuten, und hat keine Wirkung auf die Auflösung zur Laufzeit."
                               tooltipPosition="top"></i>
                        </div>
                        <div class="text-base">{{ selectedMappingTable.sourceType || '—' }}</div>
                    </div>
                    <div class="col-12 md:col-6 mb-2">
                        <div class="text-sm text-gray-600 flex align-items-center gap-1">
                            Ziel-Typ
                            <i class="fa-regular fa-circle-info"
                               pTooltip="Reine Beschriftung der Spalten — beschreibt, was Quell- und Zielwerte bedeuten, und hat keine Wirkung auf die Auflösung zur Laufzeit."
                               tooltipPosition="top"></i>
                        </div>
                        <div class="text-base">{{ selectedMappingTable.targetType || '—' }}</div>
                    </div>
                    <div class="col-12 md:col-6 mb-2">
                        <div class="text-sm text-gray-600">Fallback-Verhalten</div>
                        <div class="text-base">{{ getFallbackLabel() }}</div>
                    </div>
                    @if (showsFallbackValue()) {
                        <div class="col-12 md:col-6 mb-2">
                            <div class="text-sm text-gray-600">Fallback-Wert</div>
                            <div class="text-base">{{ selectedMappingTable.fallbackValue || '(leerer Text)' }}</div>
                        </div>
                    }
                    @if (selectedMappingTable.fallbackLogicDtoName) {
                        <div class="col-12 md:col-6 mb-2">
                            <div class="text-sm text-gray-600">Fallback-Logik</div>
                            <div class="text-base">{{ selectedMappingTable.fallbackLogicDtoName }}</div>
                        </div>
                    }
                    <div class="col-12 md:col-6 mb-2">
                        <div class="text-sm text-gray-600">Umfang</div>
                        @if (countsBusy) {
                            <p-skeleton height="1.25rem" width="10rem"></p-skeleton>
                        } @else {
                            <div class="text-base">
                                {{ entryCount === null ? '?' : entryCount }} Einträge,
                                {{ overrideCount === null ? '?' : overrideCount }} Partner-Overrides
                            </div>
                        }
                    </div>
                </div>

                @if (hasUnsetFallback()) {
                    <p-message severity="warn"
                               text="Diese Tabelle hat kein Fallback-Verhalten gesetzt. Das wirkt wie „Fehler auslösen“: jeder Nichttreffer bricht die Ausführung ab, sofern kein Vorgabewert übergeben wird."></p-message>
                }
            </div>
        }

        <!-- Immutable parameter contract -->
        <div class="surface-ground border-round-lg p-3">
            <div class="text-base font-semibold mb-1">Vertrag der Sprache</div>
            <div class="text-sm text-gray-600 mb-3">
                Diese Ein- und Ausgänge legt das System selbst an. Sie sind nicht Teil der
                Konfiguration und werden hier nur zur Orientierung angezeigt.
            </div>

            <div class="text-sm font-semibold mb-2">Eingänge</div>
            <div class="flex flex-column gap-2 mb-3">
                @for (row of contractImports; track row.alias) {
                    <div class="flex align-items-start gap-2 flex-wrap">
                        <span class="font-medium w-12rem">{{ row.alias }}</span>
                        <p-tag [value]="row.type" severity="secondary" [rounded]="true"></p-tag>
                        @if (row.mandatory) {
                            <p-tag value="Pflicht" severity="danger" [rounded]="true"></p-tag>
                        } @else {
                            <p-tag value="optional" severity="secondary" [rounded]="true"></p-tag>
                        }
                        <span class="text-sm text-gray-600 flex-1">{{ row.description }}</span>
                    </div>
                }
            </div>

            <div class="text-sm font-semibold mb-2">Ausgaben</div>
            <div class="flex flex-column gap-2 mb-3">
                @for (row of contractExports; track row.alias) {
                    <div class="flex align-items-start gap-2 flex-wrap">
                        <span class="font-medium w-12rem">{{ row.alias }}</span>
                        <p-tag [value]="row.type" severity="secondary" [rounded]="true"></p-tag>
                        <span class="text-sm text-gray-600 flex-1">{{ row.description }}</span>
                    </div>
                }
            </div>

            <p-message severity="info" [text]="absentOutputNote"></p-message>
        </div>

    </div>
}
